<template>
  <content-container :table-row-count="tableData.length">
    <!-- 搜索条件区域 -->
    <search-container>
      <search-col>
        <el-select
          v-model="params.agentId"
          filterable
          placeholder="代理商名称"
          clearable
        >
          <el-option
            v-for="item in optionsAgent"
            :key="item.id"
            :label="item.agentName"
            :value="item.id"
          />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.levelType"
          placeholder="代理商级别"
          clearable
        >
          <el-option :label="agentLevelLabel[EAgentLevel.Top]" :value="EAgentLevel.Top" />
          <el-option :label="agentLevelLabel[EAgentLevel.First]" :value="EAgentLevel.First" />
          <el-option :label="agentLevelLabel[EAgentLevel.Second]" :value="EAgentLevel.Second" />
        </el-select>
      </search-col>
      <search-col>
        <el-select
          v-model="params.serverId"
          filterable
          placeholder="服务商名称"
          clearable
        >
          <el-option
            v-for="item in optionsServer"
            :key="item.id"
            :label="item.serverName"
            :value="item.id"
          />
        </el-select>
      </search-col>
      <search-col>
        <el-date-picker
          v-model="dateRange"
          style="width: 100%"
          type="daterange"
          :disabled-date="disabledDate"
          start-placeholder="结算周期(起)"
          :default-time="defaultTimeFormat"
          format="YYYY-MM-DD"
          value-format="YYYY-MM-DD"
          end-placeholder="结算周期(止)"
          @keydown.enter="doSearch"
        />
      </search-col>

      <template #buttons>
        <el-button @click="resetForm">
          <svg-icon icon="tabler:refresh" />重置
        </el-button>
        <el-button type="primary" @click="doSearch">
          <svg-icon icon="lucide:search" />查询
        </el-button>
      </template>
    </search-container>
    <div>
      <el-button class="mb-[20px]" type="primary" @click="exportFile">
        <svg-icon icon="lucide:square-arrow-out-up-right" />导出
      </el-button>
    </div>

    <!-- 表格数据 -->
    <el-table v-loading="loading" class="normal-table" :border="true" :scrollbar-always-on="true" :data="tableData" :cell-style="initOperatorColumnStyle">
      <el-table-column align="left" type="index" label="序号" :min-width="80" />
      <el-table-column align="left" prop="settlementDate" label="结算周期" :min-width="220" />
      <el-table-column align="left" prop="agentName" label="代理商名称" :min-width="220" show-overflow-tooltip />
      <el-table-column align="left" prop="levelTypeStr" label="代理商等级" :min-width="140" />
      <el-table-column align="left" prop="serverName" label="服务商名称" :min-width="220" show-overflow-tooltip />
      <el-table-column align="right" prop="successSettleAmount" label="结算总金额(元)" :min-width="140" />
      <el-table-column align="right" prop="protectLineAmount" label="收益(元)" :min-width="140" />
      <el-table-column align="right" prop="outLineAmount" label="推广服务费(元)" :min-width="140" />
      <el-table-column align="right" prop="inLineAmount" label="溢价服务费(元)" :min-width="140" />
      <el-table-column
        v-if="buttons.length > 0"
        align="center"
        prop="actions"
        fixed="right"
        label="操作"
      >
        <template #default="scope">
          <table-row-buttons :buttons="buttons" :row="scope.row" />
        </template>
      </el-table-column>
    </el-table>

    <!-- 底部分页 -->
    <template #footer>
      <el-pagination
        v-model:current-page="params.page"
        v-model:page-size="params.size"
        :page-sizes="[defaultPageSize, 25, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @size-change="onSizeChange"
        @current-change="onCurrentPageChange"
      />
    </template>
  </content-container>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import { initOperatorColumnStyle, SvgIcon, TableRowButtons } from '@vue3/components';
import {
  ContentContainer,
  SearchContainer,
  SearchCol,
  useTblPermissionButtons,
  queryAgentListForAllReport,
  queryServerListForAllReport,

} from '@core/main';
import { agentLevelLabel, EAgentLevel } from '@core/main/types';
import { IDistributionTaskSummaryDetail, IAgentOptionsItem, IServerOptionsItem } from '../../../../types';
import { downloadFile, getFomattedTimeRange } from '@core/tools';
import { queryDistributionTaskSummaryPage, exportDistributionTaskSummary } from '@core/main/api';
import { useRoute, useRouter } from 'vue-router';

const route = useRoute();
const router = useRouter();

defineOptions({ name: 'ReportCenterAll' });

const defaultPageSize = 12;

const params = reactive<any>({
  page: 1,
  size: defaultPageSize,
  agentId: Number(route.query.agentId) || null,
  serverId: Number(route.query.serverId) || null,
  settlementDateStart: route.query.settlementDateStart || '',
  settlementDateEnd: route.query.settlementDateEnd || '',
});

const loading = ref(false);
const total = ref(0);

const tableData = ref<IDistributionTaskSummaryDetail[]>([]);

const optionsAgent = ref<IAgentOptionsItem[]>([]);
const optionsServer = ref<IServerOptionsItem[]>([]);
const dateRange = ref<any>(route.query.settlementDateStart ? [route.query.settlementDateStart, route.query.settlementDateEnd] : []);
const defaultTimeFormat = computed (() => getFomattedTimeRange());
const disabledDate = (current: Date) => current.getTime() > Date.now();

const { buttons } = useTblPermissionButtons([
  {
    name: '查看明细',
    relationField: 'levelType',
    relationValue: EAgentLevel.Top,
    action: toDetail,
  },
]);

async function exportFile() {
  try {
    const { buffer, fileName } = await exportDistributionTaskSummary({
      ...params,
      settlementDateStart: dateRange.value ? dateRange.value[0] : undefined,
      settlementDateEnd: dateRange.value ? dateRange.value[1] : undefined,
    });

    downloadFile(buffer, fileName, 'xlsx');
    ElMessage.success('导出成功');
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}
async function fetchData() {
  loading.value = true;

  try {
    const { data, total: all } = await queryDistributionTaskSummaryPage({
      ...params,
      settlementDateStart: dateRange.value ? dateRange.value[0] : undefined,
      settlementDateEnd: dateRange.value ? dateRange.value[1] : undefined,
    });

    tableData.value = data;
    total.value = all;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }

  loading.value = false;
}

function resetForm() {
  dateRange.value = [];
  for (const key in params) {
    if (!['page', 'size'].includes(key)) {
      params[key] = undefined;
    }
  }
}

function onSizeChange(size: number) {
  params.size = size;
  params.page = 1;
  fetchData();
}

function onCurrentPageChange(page: number) {
  params.page = page;
  fetchData();
}

function doSearch() {
  params.page = 1;
  fetchData();
}

function toDetail(row: IDistributionTaskSummaryDetail) {
  router.replace({
    name: 'ReportCenter',
    query: {
      activeKey: 'agent',
      agentId: row.agentId,
      serverId: row.serverId,
      settlementDateStart: row.settlementDateStart,
      settlementDateEnd: row.settlementDateEnd,
    },
  });
}
async function getAgentList() {
  try {
    const { data } = await queryAgentListForAllReport();

    optionsAgent.value = data;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function getServerList() {
  try {
    const { data } = await queryServerListForAllReport();

    optionsServer.value = data;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}
function init() {
  getAgentList();
  getServerList();
}

init();
fetchData();
</script>
